import React, { useState, useRef } from 'react';
import { Image as ImageIcon, X, Upload as UploadIcon, Loader2 } from 'lucide-react';
import { resolveCoverUrl } from '../utils/coverUrl';

/**
 * Reusable cover-image picker. Uploads to /api/upload/cover-image,
 * returns the resulting URL via onChange.
 *
 * Props:
 *   value:   string | null   — current cover URL
 *   onChange:(url|null)=>void
 */
const CoverImagePicker = ({ value, onChange, label = 'Cover image' }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const preview = value ? resolveCoverUrl(value) : null;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB.');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('cover_image', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/cover-image`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Upload failed.');
        return;
      }
      onChange?.(data.cover_image_url || data.url || null);
    } catch (e) {
      setError('Network error.');
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div data-testid="cover-image-picker">
      <p className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5">
        <ImageIcon className="w-3.5 h-3.5 text-fuchsia-300" /> {label}
        <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-1">Optional</span>
      </p>

      {!preview ? (
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="flex flex-col items-center justify-center w-full p-6 rounded-xl border-2 border-dashed border-slate-600 hover:border-fuchsia-500 bg-slate-900/40 hover:bg-slate-900/60 cursor-pointer transition-colors"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-fuchsia-300 animate-spin mb-2" />
          ) : (
            <UploadIcon className="w-6 h-6 text-gray-500 mb-2" />
          )}
          <p className="text-sm text-gray-300 font-medium">
            {uploading ? 'Uploading…' : 'Drop image or click to choose'}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">PNG · JPG · WEBP · GIF · max 10 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onPickFile}
            className="hidden"
            data-testid="cover-image-file-input"
          />
        </label>
      ) : (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-fuchsia-500/30 bg-slate-900/40">
          <img
            src={preview}
            alt="cover"
            className="w-20 h-28 object-cover rounded-md border border-slate-700"
            data-testid="cover-image-preview"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium">Cover ready ✓</p>
            <p className="text-[11px] text-gray-500 truncate">{value}</p>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="text-xs text-fuchsia-300 hover:text-fuchsia-200 underline"
                data-testid="cover-image-replace-btn"
              >
                {uploading ? 'Uploading…' : 'Replace'}
              </button>
              <button
                type="button"
                onClick={() => onChange?.(null)}
                className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                data-testid="cover-image-remove-btn"
              >
                <X className="w-3 h-3" /> Remove
              </button>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
            />
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
};

export default CoverImagePicker;
